import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../Components/UI/dialog';
import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Label } from '../../Components/UI/label';
import { RadioGroup, RadioGroupItem } from '../../Components/UI/radio-group';
import { ArrowLeft, Check, Plus, Trash2, X, ArrowLeftRight, Contact } from 'lucide-react';
import parsePhoneNumberFromString from "libphonenumber-js";
import { countries } from "../../Components/Enums/DataEnums";
import { ContactList } from "../../Components/Common/ContactList";
import { isContactPickerSupported, isMobileDevice, isIOSDevice } from "../../Utils/Utilities";
import useIsMobile from "../../Hooks/useIsMobile";
import { Checkbox } from '../../Components/UI/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../../Components/UI/card';
import MobileInput from '../../Components/Common/MobileInput';
import { toast } from 'sonner';
import { saveUser } from './UsersAPI';
import { setGlobalLoading } from '../../Store/Actions';
import { useDispatch, useSelector } from 'react-redux';
import { AlertBox } from '../../Components/Common/AlertBox';
import { getCurrentUser, validatorObj } from '../../Utils/Utilities';
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD';
import MastersDialog from '../Settings/Masters';
import TransferAssignments from './TransferAssignments/TransferAssignments';
import { checkIfInqOnlyMode, getAgencyDetails, getUsersArr, userIsCreator } from '../../Store/Reducers';
import { getAuthData } from '../../Utils/AuthUtils';

const isBasicPermissions = (p) => (
  // Only check Contacts, Inquiries, and Configuration
  p.connViewTypeID === 4 &&
  p.connEntry === true &&
  p.connDel === false &&
  p.connEdit === 2 &&
  p.thrViewTypeID === 2 &&
  p.thrActions === 2 &&
  p.agnConfig === 0
);

const isAllRightsPermissions = (p) => (
  // Only check Contacts, Inquiries, and Configuration
  p.connViewTypeID === 1 &&
  p.connEntry === true &&
  p.connDel === true &&
  p.connEdit === 1 &&
  p.thrViewTypeID === 1 &&
  p.thrActions === 1 &&
  p.agnConfig === 1
);

const resolveAccessLevel = (updatedData) => {
  if (isAllRightsPermissions(updatedData)) return "allRights";
  if (isBasicPermissions(updatedData)) return "basic";
  return "selective";
};
const UserEntry = ({ isOpen, setIsOpen, userData = null }) => {
  const dispatch = useDispatch()
  const isMobile = useIsMobile();
  const [contactList, setContactList] = useState([]);
  const [isContactListOpen, setIsContactListOpen] = useState(false);
  const [contactPermissionDenied, setContactPermissionDenied] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("91"); // Default to India
  const agencyData = useSelector(getAgencyDetails);
  const authData = getAuthData();
  const currentUserID = authData?.UserID

  const getDefaultFormData = () => ({
    name: '',
    designation: '',
    contactNo: '',
    email: '',
    otp: '',
    cc: agencyData?.CC || '91',
    regionID: 0,
    supervisorID: currentUserID || 0,
    // Set defaults to match the "Basic" profile
    accessLevel: "basic",
    fullAccess: false,
    connViewTypeID: 4,
    connEntry: true,
    connDel: false,
    connEdit: 2,
    thrViewTypeID: 2,
    thrActions: 2,
    agnConfig: 0,
    xnActions: 0,
    ctrcActions: 0,
    isAdmin: false
  });
  const [formData, setFormData] = useState(getDefaultFormData());
  const [dialogStates, setDialogStates] = useState({
    deleteAlert: false,
    masterDialog: false,
    transferAssignments: false
  });
  const [selectedRegion, setSelectedRegion] = useState("");
  const currentUser = getCurrentUser()
  const isTargetCreator = agencyData?.AdminUserID === userData?.UserID;
  const isCreatorUser = useSelector(userIsCreator); // Agencies.AdminUserID - only the creator may grant/revoke Users.IsAdmin
  const isForeignCreatorView = isTargetCreator && !isCreatorUser;
  const isLockedRecord = isTargetCreator || (formData.isAdmin && !isCreatorUser);
  const { data: connectionData } = useSelector((state) => state.connectionData);
  const { data: threadData } = useSelector((state) => state.threadData);
  const users = useSelector(getUsersArr);
  const { regions } = useSelector((state) => state.masterData);
  const showModule = { transactions: agencyData?.EnblXns, contracts: agencyData?.EnblContracts };
  const isInqOnlyMode = useSelector(checkIfInqOnlyMode);

  const getSupervisedUsers = () => {
    if (!userData?.SubUserIDs) return [];
    const subUserIDs = userData.SubUserIDs.split(',').filter(id => id.trim());
    return users.filter(user => subUserIDs.includes(user.UserID?.toString()));
  };

  const getAvailableSupervisors = () => {
    return users.filter(user =>
      user.UserID !== userData?.UserID &&
      (!user.SupervisorID)
    );
  };

  const buildFormDataFromUser = (userData) => {
    const base = {
      name: userData?.Name || '',
      designation: userData?.Designation || '',
      contactNo: userData?.Mobile || '',
      email: userData?.Email || '',
      otp: '',
      cc: userData?.CC || '91',
      fullAccess: userData?.FullAccess === true || userData?.FullAccess === 1,
      agnConfig: userData?.AgnConfig ? 1 : 0,
      connViewTypeID: userData?.ConnViewTypeID ?? 4,
      connEntry: userData?.ConnEntry === true || userData?.ConnEntry === 1,
      connDel: userData?.ConnDel === true || userData?.ConnDel === 1,
      connEdit: userData?.ConnEdit ?? 0,
      thrViewTypeID: userData?.ThrViewTypeID ?? 2,
      thrActions: userData?.ThrActions ?? 0,
      xnActions: userData?.XnActions ?? 0,
      ctrcActions: userData?.CtrcActions ?? 0,
      regionID: userData?.RegionID ?? 0,
      supervisorID: userData?.SupervisorID ?? 0,
      isAdmin: userData?.IsAdmin === true || userData?.IsAdmin === 1,
    };

    // Determine accessLevel based on permissions
    let accessLevel = "selective"; // default to custom

    const isAllRights =
      base.fullAccess &&
      base.connViewTypeID === 1 &&
      base.connEntry &&
      base.connDel &&
      base.connEdit === 1 &&
      base.agnConfig === 1 &&
      base.thrViewTypeID === 1 &&
      base.thrActions === 1 &&
      base.xnActions === 1 &&
      base.ctrcActions === 1;

    const isBasic =
      !base.fullAccess &&
      base.connViewTypeID === 4 &&
      base.connEntry &&
      !base.connDel &&
      base.connEdit === 2 &&
      base.thrViewTypeID === 2 &&
      base.thrActions === 2 &&
      base.xnActions === 0 &&
      base.ctrcActions === 0 &&
      base.agnConfig === 0;

    if (base.isAdmin && isAllRights) {
      accessLevel = "admin";
    } else if (isAllRights) {
      accessLevel = "allRights";
    } else if (isBasic) {
      accessLevel = "basic";
    }
    // else remains "selective"

    return {
      ...base,
      isAdmin: accessLevel === "admin",
      accessLevel,
    };
  };

  // Function which will reset a form data
  const resetForm = (userData) => {
    if (userData) {
      return buildFormDataFromUser(userData);
    }
    return getDefaultFormData();
  };

  // Reset form whenever dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData(resetForm(userData));
    }
  }, [isOpen, userData]);

  // Set region after new region added
  useEffect(() => {
    if (selectedRegion) {
      const rid = regions.find(rgn => rgn.Rgn == selectedRegion)?.RID || 0;
      handleFieldChange('regionID', rid);
      setSelectedRegion('')
    }
  }, [selectedRegion])

  // Effect to handle "All Rights" selection
  /* useEffect(() => {
    if (isReadonly) {
      setFormData(prev => ({
        ...prev,
        connViewTypeID: 1, // allConnections
        connEntry: true,
        connDel: true,
        connEdit: 1,
        agnConfig: 1,
        thrViewTypeID: 1,
        thrActions: 1, // All (incl. Delete)
        xnActions: 1,
        ctrcActions: 1
      }));
    }
  }, [isReadonly]); */

  // Helper function to derive checkbox states from thrActions
  const getCheckboxStates = () => {
    switch (formData.thrActions) {
      case 4: // Move
        return {
          thrMove: true,
          thrCreate: false,
          thrModify: false,
          thrDel: false,
        };
      case 3: // Create
        return {
          thrMove: true,
          thrCreate: true,
          thrModify: false,
          thrDel: false,
        };
      case 2: // Modify
        return {
          thrMove: true,
          thrCreate: true,
          thrModify: true,
          thrDel: false,
        };
      case 1: // Delete
        return {
          thrMove: true,
          thrCreate: true,
          thrModify: true,
          thrDel: true,
        };
      case 0: // None
      default:
        return {
          thrMove: false,
          thrCreate: false,
          thrModify: false,
          thrDel: false,
        };
    }
  };

  // Helper function to derive checkbox states from ctrcActions
  const getContractCheckboxStates = () => {
    switch (formData.ctrcActions) {
      case 3: // Create
        return {
          ctrcCreate: true,
          ctrcModify: false,
          ctrcDel: false,
        };
      case 2: // Modify
        return {
          ctrcCreate: true,
          ctrcModify: true,
          ctrcDel: false,
        };
      case 1: // Delete
        return {
          ctrcCreate: true,
          ctrcModify: true,
          ctrcDel: true,
        };
      case 0: // None
      default:
        return {
          ctrcCreate: false,
          ctrcModify: false,
          ctrcDel: false,
        };
    }
  };

  const openContactPicker = async () => {
    if (!isContactPickerSupported()) {
      toast.error("Contact picker not supported on this device");
      return null;
    }

    try {
      const properties = ['name', 'tel', 'email'];
      const opts = { multiple: false };

      window.contacts = null;
      setContactPermissionDenied(false);

      window.location.href = "requestcontactpermission://";

      setTimeout(() => {
        window.location.href = "readcontacts://";
      }, 300);

      const maxAttempts = 16; // ~8s at 500ms
      let attempts = 0;

      const check = setInterval(() => {
        attempts += 1;

        if (typeof window.contacts !== "undefined" && window.contacts !== null && window.contacts !== "") {
          clearInterval(check);
          let parsedContacts = [];
          try {
            parsedContacts = typeof window.contacts === 'string' ? JSON.parse(window.contacts) : window.contacts;
          } catch (e) {
            console.error("JSON Parse failed for contacts:", e);
          }

          if (parsedContacts && parsedContacts.length > 0) {
            setContactList(parsedContacts);
            setContactPermissionDenied(false);
          } else {
            setContactList([]);
            setContactPermissionDenied(isIOSDevice());
          }
          setIsContactListOpen(true);
          window.contacts = null; // Clear after use
        } else if (attempts >= maxAttempts) {
          clearInterval(check);
          setContactList([]);
          setContactPermissionDenied(isIOSDevice());
          setIsContactListOpen(true);
        }
      }, 500);
      return null;
    } catch (error) {
      console.log('Contact picker error:', error);
      alert(error);
      return null;
    }
  };

  // Re-trigger the native permission prompt (used when contacts came back empty on iOS)
  const retryContactPermission = () => {
    openContactPicker();
  };

  // Parse phone number and extract country code
  const parsePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return { countryCode: '91', number: '' };

    const cleanInput = phoneNumber.replace(/\D/g, '');

    const parsed = parsePhoneNumberFromString('+' + cleanInput);

    if (parsed && parsed.isValid()) {
      const isoCode = parsed.country;
      const countryMatch = countries.find(
        (c) => c.CountryCode.toUpperCase() === isoCode
      );

      return {
        countryCode: countryMatch?.CC || '91',
        number: parsed.nationalNumber
      };
    }

    const fallbackNumber = cleanInput.slice(-10);
    return {
      countryCode: '91',
      number: fallbackNumber
    };
  };

  // Populate form with selected contact data
  const populateFormWithContact = (contactData) => {
    if (!contactData) return;

    const updates = {};

    if (contactData.name) {
      updates.name = contactData.name;
    }

    // Logic to extract the best mobile number
    let rawPhone = contactData.phone;

    // Prioritize "mobile" or "cell" from the phoneNumbers array
    if (contactData.phoneNumbers && Array.isArray(contactData.phoneNumbers)) {
      const mobileEntry = contactData.phoneNumbers.find(p =>
        p.label?.toLowerCase().includes('mobile') ||
        p.type?.toLowerCase().includes('mobile') ||
        p.label?.toLowerCase().includes('cell')
      );
      if (mobileEntry) {
        rawPhone = mobileEntry.number || mobileEntry.value;
      }
    }

    if (rawPhone) {
      const { countryCode, number } = parsePhoneNumber(rawPhone);
      updates.contactNo = number;

      if (countryCode) {
        updates.cc = countryCode;
        setSelectedCountryCode(countryCode);
      }
    }

    if (contactData.email && contactData.email !== '-') {
      updates.email = contactData.email;
    } else if (contactData.email === '-') {
      updates.email = '';
    }

    if (contactData.org && contactData.org !== '-') {
      updates.designation = contactData.org;
    }

    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({ ...prev, ...updates }));
      toast.success("Contact information selected.");
    }
  };

  // Handle contact picker button click
  const handleContactPickerClick = async () => {
    if (!isMobileDevice()) {
      toast.info("Contact picker is only available on mobile devices");
      return;
    }

    if (!isContactPickerSupported()) {
      toast.error("Contact picker not supported on this device");
      return;
    }

    try {
      const contactData = await openContactPicker();
      if (contactData) {
        populateFormWithContact(contactData);
      }
    } catch (error) {
      console.error('Error in handleContactPickerClick:', error);
      // Fallback to contact list dialog if native picker fails
      setIsContactListOpen(true);
    }
  };

  const handleThreadCheckboxChange = (field, checked) => {
    const threadOrder = ['thrMove', 'thrCreate', 'thrModify', 'thrDel'];
    const currentIndex = threadOrder.indexOf(field);

    setFormData((prev) => {
      let newThrActions = prev.thrActions;
      if (checked) {
        const thrActionsMap = { thrDel: 1, thrModify: 2, thrCreate: 3, thrMove: 4 };
        newThrActions = thrActionsMap[field];
      } else {
        const nextLowerIndex = currentIndex - 1;
        newThrActions = nextLowerIndex < 0 ? 0 : [0, 1, 2, 3, 4][nextLowerIndex + 1];
        // Simplified logic to step down
        const stepDown = { thrDel: 2, thrModify: 3, thrCreate: 4, thrMove: 0 };
        newThrActions = stepDown[field];
      }

      const nextValues = { ...prev, thrActions: newThrActions };
      const newLevel = resolveAccessLevel(nextValues);
      return {
        ...nextValues,
        accessLevel: newLevel,
        fullAccess: newLevel === "allRights"
      };
    });
  };
  const handleConnectionCheckboxChange = (field, checked) => {
    setFormData(prev => {
      let nextValues = { ...prev, [field]: checked };

      if (field === 'connEntry' && !checked) nextValues.connDel = false;
      else if (field === 'connDel' && checked) nextValues.connEntry = true;

      const newLevel = resolveAccessLevel(nextValues);
      return {
        ...nextValues,
        accessLevel: newLevel,
        fullAccess: newLevel === "allRights" // Sync the API flag
      };
    });
  };

  const handleConfigurationRadio = (value) => {
    const agnConfigVal = parseInt(value);
    setFormData((prev) => {
      const nextValues = { ...prev, agnConfig: agnConfigVal };
      const newLevel = resolveAccessLevel(nextValues);
      return { ...nextValues, accessLevel: newLevel, fullAccess: newLevel === "allRights" };
    });
  };

  const handleFieldChange = (field, value) => {
    if (value === 'add') {
      setDialogStates(prev => ({ ...prev, masterDialog: true }));
      return;
    }
    setFormData(prev => {
      const nextValues = { ...prev, [field]: value };
      const newLevel = resolveAccessLevel(nextValues);
      return { ...nextValues, accessLevel: newLevel, fullAccess: newLevel === "allRights" };
    });
  };
  // Get current checkbox states for rendering
  const checkboxStates = getCheckboxStates();
  const contractCheckboxStates = getContractCheckboxStates();

  // Validation function
  const validateFields = () => {
    if (!formData.name.trim()) {
      return 'Please enter a name.';
    }

    // if (!formData.designation.trim()) {
    //   return 'Please enter a designation.';
    // }

    if (!formData.contactNo.trim()) {
      return 'Please enter a contact number.';
    } else if (!validatorObj.validateMobileByCC(formData.contactNo, formData.cc)) {
      return 'Please enter a valid contact number.';
    }

    // Email validation only if provided
    if (formData.email.trim() && !validatorObj.validateEmail(formData.email)) {
      return 'Please enter a valid email address';
    }

    return null;
  };



  // Handle mobile verification
  // const handleVerifyMobile = async () => {
  //   const fromError = validateFields();
  //   if (fromError) {
  //     toast.error(fromError);
  //     return;
  //   }
  //   const duplicateUser = users.find((user) =>
  //     String(user.Mobile) === String(formData?.contactNo)
  //     || (formData?.email != "" && String(user.Email) === String(formData?.email))
  //   );
  //   if (duplicateUser) {
  //     if (String(duplicateUser.Mobile) === String(formData?.contactNo)) {
  //       toast.error("This number is already registered with your agency.");
  //     } else {
  //       toast.error("This email address is already registered with your agency.");
  //     }
  //     return;
  //   }

  //   dispatch(setGlobalLoading(true));
  //   try {
  //     const response = await verifyMobile(formData.contactNo, formData.cc, false);

  //     if (response.error === false) {
  //       setIsMobileVerified(true);
  //       setHasResent(false); // Reset resend state when new OTP is sent
  //       toast.success(response.message);
  //     } else {
  //       // Show error message from C# backend
  //       toast.error(response.message || 'Failed to send OTP. Please try again.');
  //     }
  //   } catch (error) {
  //     toast.error(error.message);
  //   } finally {
  //     dispatch(setGlobalLoading(false));
  //   }
  const handleContractCheckboxChange = (field, checked) => {
    let newCtrcActions = formData.ctrcActions;
    if (checked) {
      const ctrcActionsMap = { ctrcDel: 1, ctrcModify: 2, ctrcCreate: 3 };
      newCtrcActions = ctrcActionsMap[field];
    } else {
      const nextLowerMap = { ctrcDel: 2, ctrcModify: 3, ctrcCreate: 0 };
      newCtrcActions = nextLowerMap[field];
    }
    setFormData((prev) => {
      const updated = { ...prev, ctrcActions: newCtrcActions };
      return { ...updated, accessLevel: resolveAccessLevel(updated) };
    });
  };




  // Function which will transform user data
  const transformFormDataToAPI = () => {
    const isAllRights = resolveAccessLevel(formData) === "allRights";
    return {
      userID: userData?.UserID || 0,
      changeType: userData?.UserID ? "EDIT" : "ADD",
      mobile: formData.contactNo,
      cc: formData.cc,
      name: formData.name,
      email: formData.email,
      designation: formData.designation,
      pin: formData.otp,
      bizTarget: 0,
      fullAccess: formData.fullAccess,
      agnConfig: formData.fullAccess,
      connViewTypeID: formData.connViewTypeID,
      connEntry: formData.connEntry ? 1 : 0,
      connEdit: formData.connEdit,
      connDel: formData.connDel ? 1 : 0,
      thrViewTypeID: formData.thrViewTypeID,
      thrActions: formData.thrActions,
      xnActions: isAllRights ? 1 : formData.xnActions,
      ctrcActions: isAllRights ? 1 : formData.ctrcActions,
      regionID: formData.connViewTypeID === 5 ? formData.regionID : 0,
      supervisorID: formData.supervisorID ? Number(formData.supervisorID) : 0,
      agnConfig: formData.agnConfig,
      isAdmin: formData.isAdmin ? 1 : 0
    };
  };

  // Function which will handle user entry and update user
  const handleSaveUser = async () => {
    if (isForeignCreatorView) {
      toast.error("You do not have permission to modify the account creator.");
      return;
    }

    // Get first validation error
    let formError = validateFields();
    if (formError) {
      toast.error(formError);
      return;
    }

    if (!userData) {
      const duplicateUser = users.find((user) =>
        user.Status !== 2 && user.Status !== "2" && (
          String(user.Mobile) === String(formData?.contactNo) ||
          (formData?.email && formData.email.trim() !== "" && String(user.Email)?.toLowerCase() === String(formData?.email)?.toLowerCase())
        )
      );
      if (duplicateUser) {
        if (String(duplicateUser.Mobile) === String(formData?.contactNo)) {
          toast.error("This number is already registered with your agency.");
        } else {
          toast.error("This email address is already registered with your agency.");
        }
        return;
      }
    }

    if (formData.connViewTypeID == 5 && formData.regionID == 0) {
      toast.error("Please select a region.");
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      const apiData = transformFormDataToAPI();
      const response = await saveUser(apiData);

      if (response.error === false) {
        toast.success(userData ? 'User updated.' : 'User added. Invitation sent.');
        setIsOpen(false);
      }
    } catch (error) {
      toast.error(error.message || error.ErrMsg || 'Failed to save user.');
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  const [assignmentErrorMessage, setAssignmentErrorMessage] = useState('');

  const isAssignmentError = (msg) => {
    if (!msg || typeof msg !== 'string') return false;
    return /(cannot be deleted as|assigned|supervisor|one or more)/i.test(msg);
  };

  const promptTransferConfirmation = (msg) => {
    setAssignmentErrorMessage(msg || 'This user has assigned contacts, threads, or tasks.');
    setDialogStates(prev => ({ ...prev, transferConfirmAlert: true }));
  };

  const triggerUserDeleteAPI = async () => {
    dispatch(setGlobalLoading(true));
    try {
      let apiData = transformFormDataToAPI();
      apiData = {
        ...apiData,
        changeType: 'DLT',
      };
      const response = await saveUser(apiData);
      if (response.error === false) {
        toast.success('User Deleted.');
        setIsOpen(false);
        return true;
      } else {
        const errMsg = response.message || '';
        if (isAssignmentError(errMsg)) {
          promptTransferConfirmation(errMsg);
        } else {
          toast.error(errMsg || 'Failed to delete user. Please try again.');
        }
        return false;
      }
    } catch (error) {
      const errMsg = error.message || '';
      if (isAssignmentError(errMsg)) {
        promptTransferConfirmation(errMsg);
      } else {
        toast.error(errMsg || 'Failed to delete user. Please try again.');
      }
      return false;
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  // Function which will handle delete user
  const handleDeleteUser = async () => {
    if (isTargetCreator) {
      toast.error("The account creator cannot be deleted.");
      return;
    }

    await triggerUserDeleteAPI();
  };

  const handleTransferSuccess = () => {
    if (dialogStates.isPendingDeleteAfterTransfer) {
      setDialogStates(prev => ({ ...prev, isPendingDeleteAfterTransfer: false }));
      triggerUserDeleteAPI();
    }
  };

  const mobileTitle = userData ? 'Edit User' : 'User Entry';
  const desktopTitle = userData ? `Edit User - ${userData?.Name}` : 'User Entry';

  // Get supervised users for display
  const supervisedUsers = getSupervisedUsers();
  const availableSupervisors = getAvailableSupervisors();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="max-w-[100vw] max-h-[100vh] 3xl:!max-w-[60vw] 2xl:!max-w-[70vw] xl:!max-w-[80vw] transition-all duration-300"
      >
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="flex items-center gap-2 truncate">
                  <span>{mobileTitle}</span>
                  {(userData?.Status === 0 || userData?.Status === "0") && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-800 border border-gray-400">
                      Pending
                    </span>
                  )}
                  {(userData?.Status === 2 || userData?.Status === "2") && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-red-50 text-red-700 border border-red-200">
                      Declined
                    </span>
                  )}
                </div>
              </DialogTitle>
              {userData && !isForeignCreatorView && <Button
                variant="ghost"
                size="default"
                className="p-2 w-auto h-auto hover:bg-white/10 hover:text-white"
                onClick={() => setDialogStates(prev => ({ ...prev, transferAssignments: true }))}
              >
                <ArrowLeftRight className="h-[20px] w-[20px]" />
              </Button>}
              {userData && isCreatorUser && !isTargetCreator && <Button
                variant="ghost"
                size="default"
                className="p-2 w-auto h-auto hover:bg-white/10 hover:text-white"
                onClick={() => setDialogStates(prev => ({ ...prev, deleteAlert: true }))}
              >
                <Trash2 className="h-[20px] w-[20px]" />
              </Button>}
            </div>
          </div>
          <DialogTitle className="hidden sm:flex items-center gap-3 text-lg md:text-xl">
            <span>{desktopTitle}</span>
            {(userData?.Status === 0 || userData?.Status === "0") && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 border border-gray-400">
                Pending
              </span>
            )}
            {(userData?.Status === 2 || userData?.Status === "2") && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                Declined
              </span>
            )}
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            {userData && !isForeignCreatorView && <Button
              variant="ghost"
              size="default"
              className="p-2 w-auto h-auto sm:text-text-color sm:hover:bg-gray-200"
              onClick={() => setDialogStates(prev => ({ ...prev, transferAssignments: true }))}
            >
              <ArrowLeftRight className="h-[20px] w-[20px] " />
            </Button>}
            {userData && isCreatorUser && !isTargetCreator && <Button
              variant="ghost"
              size="default"
              className="p-2 w-auto h-auto sm:text-text-color sm:hover:bg-gray-200"
              onClick={() => setDialogStates(prev => ({ ...prev, deleteAlert: true }))}
            >
              <Trash2 className="h-[20px] w-[20px]" />
            </Button>}
            <DialogClose className="flex-shrink-0" tabIndex={-1}>
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 space-y-4">
          {/* First Row - All Basic Information Fields */}
          <fieldset disabled={isForeignCreatorView} className="m-0 p-0 border-0 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-sm font-medium" required={true}>
                Name
              </Label>
              <div className="relative">
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="e.g. John Doe"
                  className={`h-11 ${isMobile && isContactPickerSupported() ? 'pr-12' : ''}`}
                  autoComplete="off"
                  required
                />
                {isMobile && isContactPickerSupported() && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleContactPickerClick}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 p-1.5 hover:bg-transparent"
                    title="Pick from contacts"
                  >
                    <Contact className="h-5 w-5" />
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="designation" className="text-sm font-medium">
                Designation
              </Label>
              <Input
                id="designation"
                value={formData.designation}
                onChange={(e) => handleFieldChange('designation', e.target.value)}
                placeholder="e.g. Manager"
                className="h-11"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contactNo" className="text-sm font-medium" required={true}>
                Mobile
              </Label>
              <MobileInput
                value={formData.contactNo}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    handleFieldChange('contactNo', val);
                  }
                }}
                placeholder="e.g. 9876543210"
                className="space-y-0"
                disabled={!!userData} // Disable in edit mode
                selectedCountryCode={formData.cc}
                onCountryCodeChange={(newCC) =>
                  setFormData((prev) => ({ ...prev, cc: newCC }))
                }
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                placeholder="e.g. john.doe@gmail.com"
                className="h-11"
                autoComplete="off"
              />
            </div>
          </fieldset>

          {/* Access Level and Supervisor Section */}
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
            {/* Access Level Section - Show second on mobile, first on desktop */}
            <Card className="bg-sahi-white h-full flex py-3 order-last lg:order-first lg:col-span-3">
              <CardContent className="flex flex-col sm:flex-row gap-3 px-4 w-full">
                <Label className="font-semibold min-w-24">Access Level:</Label>
                <RadioGroup
                  value={formData.accessLevel}
                  onValueChange={(value) => {
                    let updates = { isAdmin: false };
                    if (value === "basic") {
                      updates = {
                        ...updates,
                        fullAccess: false,
                        connViewTypeID: 4,
                        connEntry: true,
                        connDel: false,
                        connEdit: 2,
                        thrViewTypeID: 2,
                        thrActions: 2,
                        agnConfig: 0
                      };
                    } else if (value === "allRights" || value === "admin") {
                      updates = {
                        ...updates,
                        fullAccess: true,
                        connViewTypeID: 1,
                        connEntry: true,
                        connDel: true,
                        connEdit: 1,
                        thrViewTypeID: 1,
                        thrActions: 1,
                        agnConfig: 1,
                        xnActions: 1,
                        ctrcActions: 1,
                        isAdmin: value === "admin"
                      };
                    }
                    setFormData(prev => ({ ...prev, ...updates, accessLevel: value }));
                  }}
                  className="flex flex-wrap gap-4"
                  disabled={isLockedRecord}
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="basic" id="basic" />
                    <Label htmlFor="basic" className="cursor-pointer">Basic</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="selective" id="selective" />
                    <Label htmlFor="selective" className="cursor-pointer">Custom</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="allRights" id="allRights" />
                    <Label htmlFor="allRights" className="cursor-pointer">All Rights</Label>
                  </div>
                  {isCreatorUser && !isTargetCreator && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="admin" id="admin" />
                      <Label htmlFor="admin" className="cursor-pointer">Admin</Label>
                    </div>
                  )}
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Supervisor/Supervising Section - Show first on mobile, last on desktop */}
            {!isTargetCreator && (
              <div className="order-first lg:order-last lg:col-span-3">

                {userData?.SubUserIDs && userData.SubUserIDs.trim() !== '' ? (
                  // Show supervised users when user is a supervisor (has SubUserIDs) - with white background
                  <Card className="bg-sahi-white h-full py-3">
                    <CardContent className="px-4 h-full flex items-center">
                      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                        <Label className="text-sm font-semibold">Subordinates:</Label>
                        <div className="flex flex-wrap items-center gap-2">
                          {supervisedUsers.length > 0 ? (
                            supervisedUsers.map((user) => (
                              <span
                                key={user.UserID}
                                className="inline-flex items-center px-3 py-1 text-xs font-medium sahi-badge sahi-radius"
                              >
                                {user.Name}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No supervised users</span>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  // Show dropdown when user has no supervised users (not a supervisor) - no white background
                  <div className="flex flex-col lg:flex-row lg:items-center lg:gap-3 space-y-1.5 lg:space-y-0 h-full">
                    <Label htmlFor="supervisor" className="text-sm font-semibold min-w-24">
                      Supervisor:
                    </Label>
                    <div className="w-full lg:w-72">
                      <CustomSearchableDropdown
                        options={[
                          { value: 0, label: "None" },
                          ...availableSupervisors
                            .slice()
                            .sort((a, b) => a.Name.localeCompare(b.Name))
                            .map(user => ({
                              value: user.UserID,
                              label: user.Name
                            }))
                        ]}
                        value={formData.supervisorID}
                        onSelect={(val) => handleFieldChange("supervisorID", val)}
                        label=""
                        placeholder="Select Supervisor"
                        fieldID="supervisorSelect"
                        searchEnabled={true}
                        allowAdd={false}
                        className="h-11"
                        noSort={true}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Second Row - Connections */}
          <fieldset disabled={isLockedRecord} className={`m-0 p-0 border-0 grid grid-cols-1 gap-4 ${(showModule.contracts && showModule.transactions) ? '' : 'lg:grid-cols-2'}`}>
            <Card className="bg-sahi-white h-full py-3">
              <CardHeader className="gap-0">
                <CardTitle className="text-base">Contacts</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 px-4">
                {/* Connection Access */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <Label className="text-sm font-medium min-w-20">Access<span className="hidden lg:inline">:</span></Label>
                  <RadioGroup
                    value={formData.connViewTypeID?.toString()}
                    onValueChange={(value) => {
                      const val = parseInt(value);
                      setFormData(prev => {
                        let updates = { connViewTypeID: val };

                        // If Contact Access is set to "Assigned Only" (4), 
                        // automatically move Threads to "Assigned Only" (2)
                        if (val === 4) {
                          updates.thrViewTypeID = 2;
                        }

                        const nextValues = { ...prev, ...updates };
                        const newLevel = resolveAccessLevel(nextValues);
                        return { ...nextValues, accessLevel: newLevel, fullAccess: newLevel === "allRights" };
                      });
                    }}
                    className="flex flex-wrap gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="4" id="connAssignedOnly" />
                      <Label htmlFor="connAssignedOnly" className="cursor-pointer">Assigned only</Label>
                    </div>
                    {isInqOnlyMode ? (
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="1" id="allConnections" />
                        <Label htmlFor="allConnections" className={`cursor-pointer `}>All</Label>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="3" id="allSuppliers" />
                          <Label htmlFor="allSuppliers" className={`cursor-pointer `}>All Suppliers</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="2" id="allCustomers" />
                          <Label htmlFor="allCustomers" className={`cursor-pointer `}>All Customers</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="1" id="allConnections" />
                          <Label htmlFor="allConnections" className={`cursor-pointer `}>All Contacts</Label>
                        </div>
                      </>
                    )}

                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                      {regions?.length > 0 && <div className="flex items-center space-x-2">
                        <RadioGroupItem value="5" id="selectedRegion" />
                        <Label htmlFor="selectedRegion" className={`cursor-pointer `}>Selected Region</Label>
                      </div>}

                      {(formData.connViewTypeID === 5 && regions?.length > 0) && (
                        <div className="w-60 sm:ml-2 sm:min-w-[200px] sm:w-auto">
                          <CustomSearchableDropdown
                            options={regions.map(r => ({ value: r.RID, label: r.Rgn }))}
                            value={formData.regionID}
                            onSelect={(val) => handleFieldChange("regionID", val)}
                            label="Region"
                            placeholder="e.g. North-Gujarat"
                            fieldID="regionSelect"
                            searchEnabled={false}
                            allowAdd={true}
                            addText={`Add new region`}
                          />
                        </div>
                      )}
                    </div>
                  </RadioGroup>
                </div>

                {/* Connection Entry */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                  <Label className="text-sm font-medium min-w-20">Entry<span className="hidden lg:inline">:</span></Label>
                  <div className="flex gap-4">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="connEntry"
                        checked={formData.connEntry}
                        onCheckedChange={(checked) => handleConnectionCheckboxChange('connEntry', checked)}

                      />
                      <Label htmlFor="connEntry" className={`cursor-pointer `}>Create</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="connDel" // Updated id
                        checked={formData.connDel}
                        onCheckedChange={(checked) => handleConnectionCheckboxChange('connDel', checked)}
                        disabled={!formData.connEntry}
                      />
                      <Label htmlFor="connDel" className={`cursor-pointer ${(!formData.connEntry) ? 'text-gray-400' : ''}`}>Delete</Label>
                    </div>
                  </div>
                </div>

                {/* Connection Update */}
                <div className="flex flex-col sm:flex-row sm:items-center items-start gap-3">
                  <Label className="text-sm font-medium min-w-20">Update<span className="hidden lg:inline">:</span></Label>
                  <RadioGroup
                    value={formData.connEdit?.toString()}
                    onValueChange={(value) => handleFieldChange('connEdit', parseInt(value))}
                    className="flex gap-4 flex-wrap"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="0" id="connUpdateNone" />
                      <Label htmlFor="connUpdateNone" className={`cursor-pointer `}>None</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="2" id="onlyMissingFields" />
                      <Label htmlFor="onlyMissingFields" className={`cursor-pointer `}>Only missing fields</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1" id="allData" />
                      <Label htmlFor="allData" className={`cursor-pointer `}>All data</Label>
                    </div>
                  </RadioGroup>
                </div>
              </CardContent>
            </Card>

            {(!showModule.contracts || !showModule.transactions) && (
              <div className="flex flex-col gap-4">
                {/* Threads Section */}
                <Card className="bg-sahi-white py-3">
                  <CardHeader className="gap-0">
                    <CardTitle className="text-base">{isInqOnlyMode ? "Inquiries" : "Threads"}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 px-4">
                    {/* Thread Access */}
                    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                      <Label className="text-sm font-medium min-w-20">Access<span className="hidden lg:inline">:</span></Label>
                      <RadioGroup
                        value={formData.thrViewTypeID?.toString()}
                        onValueChange={(value) => {
                          const val = parseInt(value);
                          // If user picks "All Threads/Inquiries", we cannot have 
                          // "Assigned Only" contacts. Default them to "All Contacts" (1).
                          if (val === 1 && formData.connViewTypeID === 4) {
                            setFormData(prev => {
                              const nextValues = { ...prev, thrViewTypeID: val, connViewTypeID: 1 };
                              const newLevel = resolveAccessLevel(nextValues);
                              return { ...nextValues, accessLevel: newLevel, fullAccess: newLevel === "allRights" };
                            });
                          } else {
                            handleFieldChange('thrViewTypeID', val);
                          }
                        }}
                        className="flex flex-wrap gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="2" id="threadAssignedOnly" />
                          <Label htmlFor="threadAssignedOnly" className="cursor-pointer">Assigned only</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem
                            value="1"
                            id="allThreads"
                            disabled={formData.connViewTypeID === 4}
                          />
                          <Label
                            htmlFor="allThreads"
                            className={`cursor-pointer ${formData.connViewTypeID === 4 ? 'text-gray-400' : ''}`}
                          >
                            All {isInqOnlyMode ? "Inquiries" : "Threads"}
                          </Label>
                        </div>
                      </RadioGroup>
                    </div>

                    {/* Thread Actions - Now using checkboxes with cascading functionality */}
                    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                      <Label className="text-sm font-medium min-w-20">Actions<span className="hidden lg:inline">:</span></Label>
                      <div className="flex flex-wrap gap-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="thrMove"
                            checked={checkboxStates.thrMove}
                            onCheckedChange={(checked) => handleThreadCheckboxChange('thrMove', checked)}

                          />
                          <Label htmlFor="thrMove" className={`cursor-pointer `}>Move</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="thrCreate"
                            checked={checkboxStates.thrCreate}
                            onCheckedChange={(checked) => handleThreadCheckboxChange('thrCreate', checked)}
                            disabled={!checkboxStates.thrMove}
                          />
                          <Label htmlFor="thrCreate" className={`cursor-pointer ${(!checkboxStates.thrMove) ? 'text-gray-400' : ''}`}>Create</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="thrModify"
                            checked={checkboxStates.thrModify}
                            onCheckedChange={(checked) => handleThreadCheckboxChange('thrModify', checked)}
                            disabled={!checkboxStates.thrCreate}
                          />
                          <Label htmlFor="thrModify" className={`cursor-pointer ${(!checkboxStates.thrCreate) ? 'text-gray-400' : ''}`}>Modify</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="thrDel"
                            checked={checkboxStates.thrDel}
                            onCheckedChange={(checked) => handleThreadCheckboxChange('thrDel', checked)}
                            disabled={!checkboxStates.thrModify}
                          />
                          <Label htmlFor="thrDel" className={`cursor-pointer ${(!checkboxStates.thrModify) ? 'text-gray-400' : ''}`}>Delete</Label>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-sahi-white py-3">
                  <CardHeader className="gap-0">
                    <CardTitle className="text-base">Configuration</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 px-4">
                    {/* Contract Actions - Using checkboxes with cascading functionality (no Move) */}
                    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                      <Label className="text-sm font-medium min-w-20">Actions<span className="hidden lg:inline">:</span></Label>
                      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                        <RadioGroup
                          value={formData.agnConfig?.toString()}
                          onValueChange={(value) => handleConfigurationRadio(value)}
                          className="flex flex-wrap gap-4"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="1" id="allowChangeConfig" />
                            <Label htmlFor="allowChangeConfig" className={`cursor-pointer `}>Allow</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="0" id="notAllowChangeConfig" />
                            <Label htmlFor="notAllowChangeConfig" className={`cursor-pointer `}>Disallow</Label>
                          </div>
                        </RadioGroup>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </fieldset>

          {/* Third Row - Threads (50%) and Transactions (50%) */}
          <fieldset disabled={isLockedRecord} className={`m-0 p-0 border-0 ${(!showModule.contracts || !showModule.transactions) ? '' : "grid grid-cols-1 lg:grid-cols-2 gap-4"}`}>
            {/* Threads Section */}
            {(showModule.contracts && showModule.transactions) &&
              <Card className="bg-sahi-white py-3">
                <CardHeader className="gap-0">
                  <CardTitle className="text-base">{isInqOnlyMode ? "Inquiries" : "Threads"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 px-4">
                  {/* Thread Access */}
                  <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                    <Label className="text-sm font-medium min-w-20">Access<span className="hidden lg:inline">:</span></Label>
                    <RadioGroup
                      value={formData.thrViewTypeID?.toString()}
                      onValueChange={(value) => handleFieldChange('thrViewTypeID', parseInt(value))}
                      className="flex flex-wrap gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="2" id="threadAssignedOnly" />
                        <Label htmlFor="threadAssignedOnly" className={`cursor-pointer `}>Assigned only</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="1" id="allThreads" />
                        <Label htmlFor="allThreads" className={`cursor-pointer `}>All {isInqOnlyMode ? "Inquiries" : "Threads"}</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Thread Actions - Now using checkboxes with cascading functionality */}
                  <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                    <Label className="text-sm font-medium min-w-20">Actions<span className="hidden lg:inline">:</span></Label>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="thrMove"
                          checked={checkboxStates.thrMove}
                          onCheckedChange={(checked) => handleThreadCheckboxChange('thrMove', checked)}

                        />
                        <Label htmlFor="thrMove" className={`cursor-pointer `}>Move</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="thrCreate"
                          checked={checkboxStates.thrCreate}
                          onCheckedChange={(checked) => handleThreadCheckboxChange('thrCreate', checked)}
                          disabled={!checkboxStates.thrMove}
                        />
                        <Label htmlFor="thrCreate" className={`cursor-pointer ${(!checkboxStates.thrMove) ? 'text-gray-400' : ''}`}>Create</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="thrModify"
                          checked={checkboxStates.thrModify}
                          onCheckedChange={(checked) => handleThreadCheckboxChange('thrModify', checked)}
                          disabled={!checkboxStates.thrCreate}
                        />
                        <Label htmlFor="thrModify" className={`cursor-pointer ${(!checkboxStates.thrCreate) ? 'text-gray-400' : ''}`}>Modify</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="thrDel"
                          checked={checkboxStates.thrDel}
                          onCheckedChange={(checked) => handleThreadCheckboxChange('thrDel', checked)}
                          disabled={!checkboxStates.thrModify}
                        />
                        <Label htmlFor="thrDel" className={`cursor-pointer ${(!checkboxStates.thrModify) ? 'text-gray-400' : ''}`}>Delete</Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>}

            {/* Transactions Section */}
            {showModule.transactions && (
              <Card className="bg-sahi-white py-3">
                <CardHeader className="gap-0">
                  <CardTitle className="text-base">Transactions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 px-4">
                  {/* Transaction Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-start gap-2">
                    <Label className="text-sm font-medium min-w-20">Actions<span className="hidden lg:inline">:</span></Label>
                    <RadioGroup
                      value={formData.xnActions?.toString()}
                      onValueChange={(value) => handleFieldChange('xnActions', parseInt(value))}
                      className="flex flex-wrap gap-3"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="0" id="transNone" />
                        <Label htmlFor="transNone" className={`cursor-pointer `}>None</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="3" id="transCreate" />
                        <Label htmlFor="transCreate" className={`cursor-pointer `}>Create</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="2" id="transModify" />
                        <Label htmlFor="transModify" className={`cursor-pointer `}>Modify</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="1" id="transAll" />
                        <Label htmlFor="transAll" className={`cursor-pointer `}>All</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </CardContent>
              </Card>
            )}
          </fieldset>

          {/* Fourth Row - Contracts (50%) */}
          <fieldset disabled={isLockedRecord} className={`m-0 p-0 border-0 ${(!showModule.contracts && !showModule.transactions) ? '' : 'grid grid-cols-1 lg:grid-cols-2 gap-4'}`}>
            {/* Contracts Section */}
            {showModule.contracts && (
              <Card className="bg-sahi-white py-3">
                <CardHeader className="gap-0">
                  <CardTitle className="text-base">Contracts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 px-4">
                  {/* Contract Actions - Using checkboxes with cascading functionality (no Move) */}
                  <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                    <Label className="text-sm font-medium min-w-20">Actions<span className="hidden lg:inline">:</span></Label>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="ctrcCreate"
                          checked={contractCheckboxStates.ctrcCreate}
                          onCheckedChange={(checked) => handleContractCheckboxChange('ctrcCreate', checked)}

                        />
                        <Label htmlFor="ctrcCreate" className={`cursor-pointer `}>Create / Renew</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="ctrcModify"
                          checked={contractCheckboxStates.ctrcModify}
                          onCheckedChange={(checked) => handleContractCheckboxChange('ctrcModify', checked)}
                          disabled={!contractCheckboxStates.ctrcCreate}
                        />
                        <Label htmlFor="ctrcModify" className={`cursor-pointer ${(!contractCheckboxStates.ctrcCreate) ? 'text-gray-400' : ''}`}>Modify</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="ctrcDel"
                          checked={contractCheckboxStates.ctrcDel}
                          onCheckedChange={(checked) => handleContractCheckboxChange('ctrcDel', checked)}
                          disabled={!contractCheckboxStates.ctrcModify}
                        />
                        <Label htmlFor="ctrcDel" className={`cursor-pointer ${(!contractCheckboxStates.ctrcModify) ? 'text-gray-400' : ''}`}>Delete</Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {(showModule.contracts && showModule.transactions) && <Card className="bg-sahi-white py-3">
              <CardHeader className="gap-0">
                <CardTitle className="text-base">Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 px-4">
                {/* Contract Actions - Using checkboxes with cascading functionality (no Move) */}
                <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                  <Label className="text-sm font-medium min-w-20">Actions<span className="hidden lg:inline">:</span></Label>
                  <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3">
                    <RadioGroup
                      value={formData.agnConfig?.toString()}
                      onValueChange={(value) => handleConfigurationRadio(value)}
                      className="flex flex-wrap gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="1" id="allowChangeConfig" />
                        <Label htmlFor="allowChangeConfig" className={`cursor-pointer `}>Allow</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="0" id="notAllowChangeConfig" />
                        <Label htmlFor="notAllowChangeConfig" className={`cursor-pointer `}>Disallow</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>
              </CardContent>
            </Card>}
          </fieldset>


          {/* Action Button */}
          <div className="pb-4 flex">
            <Button
              onClick={handleSaveUser}
              className="w-full sm:w-auto"
              disabled={isForeignCreatorView}
            >
              <Check className="w-5 h-5" />
              {userData ? 'Update User' : 'Add User'}
            </Button>
            <Button
              onClick={() => setIsOpen(false)}
              variant="secondary"
              className="w-full sm:ml-2 sm:w-auto hidden sm:flex"
            >
              <X className="w-5 h-5" />
              Close
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* Task complete alert box */}
      <AlertBox
        open={dialogStates.deleteAlert}
        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, deleteAlert: open }))}
        title="Delete User"
        description="Are you sure you wish to delete this user?"
        actions={[
          { label: "No", value: 0, variant: "cancel", icon: <X className="h-4 w-4" /> },
          { label: "Yes", value: 1, icon: <Check className="h-4 w-4" /> },
        ]}
        onAction={(val) => {
          if (val === 1) {
            handleDeleteUser(userData)
          }
        }}
      />

      {/* Transfer assignment confirmation alert box */}
      <AlertBox
        open={dialogStates.transferConfirmAlert}
        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, transferConfirmAlert: open }))}
        title="Transfer Assignments & Delete"
        description={`${assignmentErrorMessage || 'This user has assigned contacts, threads, or tasks.'} Do you wish to transfer assignments and proceed to delete?`}
        actions={[
          { label: "No", value: 0, variant: "cancel", icon: <X className="h-4 w-4" /> },
          { label: "Yes", value: 1, icon: <Check className="h-4 w-4" /> },
        ]}
        onAction={(val) => {
          setDialogStates(prev => ({ ...prev, transferConfirmAlert: false }));
          if (val === 1) {
            setDialogStates(prev => ({ ...prev, transferAssignments: true, isPendingDeleteAfterTransfer: true }));
          }
        }}
      />

      {/* region dialog box */}
      {dialogStates.masterDialog && <MastersDialog
        open={dialogStates.masterDialog}
        onClose={() => {
          setDialogStates(prev => ({ ...prev, masterDialog: false }));
        }}
        directAdd={true}
        openEntry={true}
        masterType='regions'
        onProductAdded={(itemName) => {
          setSelectedRegion(itemName)
          setDialogStates(prev => ({ ...prev, masterDialog: false }));
        }}
      />}

      {/* Transfer Assignment dialog */}
      {userData && <TransferAssignments
        isOpen={dialogStates.transferAssignments}
        onOpenChange={(open) => setDialogStates((prev) => ({ ...prev, transferAssignments: open, ...(!open && { isPendingDeleteAfterTransfer: false }) }))}
        user={userData}
        onSuccess={handleTransferSuccess}
      />}

      <ContactList
        externalOpen={isContactListOpen}
        onOpenChange={setIsContactListOpen}
        contacts={contactList}
        permissionDenied={contactPermissionDenied}
        onRequestPermission={retryContactPermission}
        onContactClick={(contact) => {
          populateFormWithContact(contact);
          setIsContactListOpen(false);
        }}
      />
    </Dialog>
  );
};

export default UserEntry;